import {
    DRAIN_WINDOW_FIELD_KEY,
    MAX_BURN_RATE_FIELD_KEY,
    MICRO_REFILL_FIELD_KEY,
    OFFLINE_GAP_CAP_FIELD_KEY,
    normalizePolicyOptionId,
} from './vehiclePolicyConfig';
import { normalizeFuelPolicyFromApi } from '../fuelConfigurator/fuelPolicyApiMapping';

/**
 * Fuel tank setup fields copied from the linked Fuel Configurator policy (tracking tolerances).
 */
export const FUEL_SETUP_FIELD_KEYS_FILLED_BY_FUEL_POLICY = [
    'Maximum Tank Capacity',
    'Minimum Refill Detection',
    'Theft/Drain Detection Alert',
    'Sensor Noise/Spike Filter (L)',
    'Fuel Stabilization Wait Time (Sec)',
    'Allowed Stabilization Fluctuation (L)',
    MICRO_REFILL_FIELD_KEY,
    OFFLINE_GAP_CAP_FIELD_KEY,
    DRAIN_WINDOW_FIELD_KEY,
    MAX_BURN_RATE_FIELD_KEY,
];

export const FUEL_SETUP_FIELD_KEYS_FILLED_BY_FUEL_POLICY_SET = new Set(
    FUEL_SETUP_FIELD_KEYS_FILLED_BY_FUEL_POLICY,
);

export const isFuelSetupFieldFilledByFuelPolicy = (fieldKey) => (
    FUEL_SETUP_FIELD_KEYS_FILLED_BY_FUEL_POLICY_SET.has(fieldKey)
);

/**
 * GNSS fields copied from the linked Fuel Configurator policy (Tier 0 coefficients).
 * Speed-zone fields and enable toggles stay manual on the vehicle preset.
 */
export const GNSS_FIELD_KEYS_FILLED_BY_FUEL_POLICY = [
    'Idling Burn Rate (L/hr)',
    'City Consumption Rate (L/100km)',
    'Average Consumption Rate (L/100km)',
    'Highway Consumption Rate (L/100km)',
    'Overspeeding Wind Resistance Penalty (%)',
    'Overspeeding Penalty Interval (km/h)',
];

export const GNSS_FIELD_KEYS_FILLED_BY_FUEL_POLICY_SET = new Set(
    GNSS_FIELD_KEYS_FILLED_BY_FUEL_POLICY,
);

export const isGnssFieldFilledByFuelPolicy = (fieldKey) => (
    GNSS_FIELD_KEYS_FILLED_BY_FUEL_POLICY_SET.has(fieldKey)
);

const FUEL_POLICY_COEFF_KEYS_BY_VEHICLE_FIELD = {
    'Idling Burn Rate (L/hr)': ['idleLhr', 'idleLHr'],
    'City Consumption Rate (L/100km)': ['cityL100km', 'cityL100Km'],
    'Average Consumption Rate (L/100km)': ['avgL100km', 'avgL100Km'],
    'Highway Consumption Rate (L/100km)': ['hwyL100km', 'hwyL100Km'],
    'Overspeeding Wind Resistance Penalty (%)': ['penaltyPct', 'penaltyPercent'],
    'Overspeeding Penalty Interval (km/h)': ['penaltyIntervalKmh', 'penaltyInterval'],
};

const pickFirstDefined = (source, keys) => {
    if (!source || typeof source !== 'object') return undefined;
    for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i];
        const value = source[key];
        if (value !== undefined && value !== null && value !== '') {
            return value;
        }
    }
    return undefined;
};

/** Resolve GNSS coefficient object from a fuel policy list item (API shape may vary). */
export const extractFuelPolicyGnssCoefficients = (policy = {}) => (
    policy?.gnssVirtualFuelCoefficients
    ?? policy?.gnssVirtualFuel
    ?? policy?.gnssCoefficients
    ?? policy?.gnss
    ?? policy?.tier0
    ?? policy
);

/** Map fuel policy tracking tolerances → vehicle preset fuel tank setup field value strings. */
export const mapFuelPolicyToFuelSetupFieldValues = (policy) => {
    const normalized = normalizeFuelPolicyFromApi(policy);
    if (!normalized) return {};

    const mapping = {
        'Maximum Tank Capacity': normalized.tankCapacityL,
        'Minimum Refill Detection': normalized.refillDetectionThresholdL,
        'Theft/Drain Detection Alert': normalized.theftDrainThresholdL,
        'Sensor Noise/Spike Filter (L)': normalized.spikeFilterVolumeL,
        'Fuel Stabilization Wait Time (Sec)': normalized.sloshSettleDurationSec,
        'Allowed Stabilization Fluctuation (L)': normalized.sloshToleranceL,
        [MICRO_REFILL_FIELD_KEY]: normalized.microRefillL ?? '',
        [OFFLINE_GAP_CAP_FIELD_KEY]: normalized.gapCapMin,
        [DRAIN_WINDOW_FIELD_KEY]: normalized.drainSpanMin,
        [MAX_BURN_RATE_FIELD_KEY]: normalized.maxBurnRateLh,
    };

    const patch = {};
    FUEL_SETUP_FIELD_KEYS_FILLED_BY_FUEL_POLICY.forEach((fieldKey) => {
        const raw = mapping[fieldKey];
        patch[fieldKey] = raw !== undefined && raw !== null && raw !== '' ? String(raw) : '';
    });
    return patch;
};

/** Map fuel policy GNSS coefficients → vehicle preset field value strings. */
export const mapFuelPolicyToGnssFieldValues = (policy) => {
    const source = extractFuelPolicyGnssCoefficients(policy);
    const patch = {};
    GNSS_FIELD_KEYS_FILLED_BY_FUEL_POLICY.forEach((fieldKey) => {
        const raw = pickFirstDefined(source, FUEL_POLICY_COEFF_KEYS_BY_VEHICLE_FIELD[fieldKey]);
        if (raw !== undefined) {
            patch[fieldKey] = String(raw);
        }
    });
    return patch;
};

/** Map linked fuel policy → all vehicle preset fields filled from that policy. */
export const mapFuelPolicyToVehiclePresetFieldValues = (policy) => ({
    ...mapFuelPolicyToFuelSetupFieldValues(policy),
    ...mapFuelPolicyToGnssFieldValues(policy),
});

export const findFuelPolicyRecordById = (records, policyId) => {
    const normalizedId = normalizePolicyOptionId(policyId);
    if (!normalizedId || !Array.isArray(records)) return null;
    return records.find(
        (item) => normalizePolicyOptionId(item?.oid || item?.id || item?._id) === normalizedId,
    ) || null;
};
